import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Activity, RefreshCw, CheckCircle2, XCircle, Database } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function SystemStatus() {
    const { user, loading: authLoading } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchHealth = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get("/system/health");
            setData(data);
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || "Erreur de chargement";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user?.role === "ti") fetchHealth();
    }, [user, fetchHealth]);

    // Garde-rôle TI : redirige les non-TI
    if (authLoading) return null;
    if (!user || user.role !== "ti") return <Navigate to="/" replace />;

    const databases = data?.databases || {};
    const dbNames = Object.keys(databases);

    return (
        <div className="space-y-6" data-testid="system-status-page">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-3xl font-display font-bold tracking-tight text-slate-900 flex items-center gap-2">
                        <Activity size={26} strokeWidth={2.25} />
                        État du système
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Diagnostic de connexion aux bases de données legacy — visible TI uniquement.
                    </p>
                </div>
                <Button
                    onClick={fetchHealth}
                    disabled={loading}
                    variant="outline"
                    size="sm"
                    data-testid="refresh-health-btn"
                    className="rounded-md"
                >
                    <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
                    Rafraîchir
                </Button>
            </div>

            {/* Bandeau global */}
            {data && (
                <Card data-testid="system-summary-card">
                    <CardContent className="py-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                        <div className="flex items-center gap-2">
                            {data.all_ok ? (
                                <CheckCircle2 size={20} className="text-emerald-600" />
                            ) : (
                                <XCircle size={20} className="text-red-600" />
                            )}
                            <span className="font-medium text-slate-800">
                                {data.all_ok ? "Toutes les bases répondent" : "Au moins une base est en erreur"}
                            </span>
                        </div>
                        <div className="text-xs text-slate-500">
                            Mode : <span className="font-mono">{data.mode}</span>
                        </div>
                        <div className="text-xs text-slate-500">
                            Version : <span className="font-mono">{data.version}</span>
                        </div>
                        <div className="text-xs text-slate-500">
                            Heure serveur :{" "}
                            <span className="font-mono">
                                {new Date(data.server_time).toLocaleString("fr-CA")}
                            </span>
                        </div>
                    </CardContent>
                </Card>
            )}

            {error && (
                <Card>
                    <CardContent className="py-4 text-sm text-red-600" data-testid="health-error">
                        {error}
                    </CardContent>
                </Card>
            )}

            {/* Grille des bases */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="db-grid">
                {dbNames.map((name) => {
                    const db = databases[name];
                    return (
                        <Card
                            key={name}
                            className={`border-l-4 ${
                                db.ok ? "border-l-emerald-500" : "border-l-red-500"
                            }`}
                            data-testid={`db-card-${name}`}
                        >
                            <CardHeader className="pb-2">
                                <CardTitle className="flex items-center justify-between text-base">
                                    <span className="flex items-center gap-2">
                                        <Database size={16} />
                                        {name}
                                        {db.primary && (
                                            <Badge variant="secondary" className="text-[10px]">
                                                primaire
                                            </Badge>
                                        )}
                                    </span>
                                    {db.ok ? (
                                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                            OK
                                        </Badge>
                                    ) : (
                                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                                            ERREUR
                                        </Badge>
                                    )}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 text-sm">
                                <div className="flex justify-between text-slate-600">
                                    <span>Dialecte</span>
                                    <span className="font-mono text-slate-800">{db.dialect}</span>
                                </div>
                                <div className="flex justify-between text-slate-600">
                                    <span>Latence</span>
                                    <span
                                        className={`font-mono ${
                                            db.latency_ms > 500
                                                ? "text-amber-600"
                                                : "text-slate-800"
                                        }`}
                                    >
                                        {db.latency_ms} ms
                                    </span>
                                </div>
                                <div className="text-xs text-slate-500 break-all">
                                    <span className="text-slate-400">URL : </span>
                                    <span className="font-mono">{db.url}</span>
                                </div>
                                {db.error && (
                                    <div
                                        className="text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2 font-mono break-words"
                                        data-testid={`db-error-${name}`}
                                    >
                                        {db.error}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {data?.deployed_at && (
                <div className="text-xs text-slate-400" data-testid="deployed-at-info">
                    Dernier déploiement :{" "}
                    {new Date(data.deployed_at).toLocaleString("fr-CA")}
                </div>
            )}
        </div>
    );
}
